/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/TimeChart.js
   Run "node shared-ui/sync.js" after editing the source. */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './charts.css';

/* Measuring before paint avoids a one-frame jump from the 720px default.
   Guarded so the component stays quiet if it is ever rendered on a server. */
const useMeasureEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * TimeChart — change over time. One line per series over a shared x axis.
 *
 * ---------------------------------------------------------------------------
 * ONE Y AXIS, ONE SCALE. There is no dual-axis mode and there will not be one:
 * two measures of different magnitude on one axis invents a relationship that
 * is not in the data. If you have registrations (0–40) and logins (0–1200),
 * that is TWO TimeCharts stacked, not one chart with two axes. The caller is
 * responsible for that split.
 * ---------------------------------------------------------------------------
 *
 * The API returns months with no activity as ABSENT ROWS. An absent row is not
 * a zero — it is a hole, and a line drawn straight across a hole lies about the
 * shape. Fill the gaps before handing `data` over; `fillMonthGaps` (exported
 * below) does it.
 *
 * PROPS
 *   data     array of row objects, ascending in time. Values may be strings
 *            ("403") — every one is coerced with Number().
 *   series   [{ key, label, color, format }]
 *              key    — field on each row
 *              label  — legend / tooltip name
 *              color  — a TOKEN NAME: '--mark' | '--good' | '--crit' |
 *                       '--accent' | '--ink-2'. Never a hex value.
 *              format — optional (value) => string, overrides `format`
 *   xKey     field holding the x value                     (default 'ym')
 *   height   plot height in CSS px                          (default 220)
 *   format   (value, seriesKey, row) => string for values   (default el-GR)
 *   xFormat  (raw, row) => string for axis + tooltip head
 *            (default: "2026-01" -> "Ιαν 26", anything else printed as-is)
 *   area     soft fill under the line. Only honoured for a single series.
 *            (default: true when series.length === 1)
 *   loading  render the skeleton instead of the chart
 *   empty    text shown when there is nothing to draw
 *   caption  short factual line under the chart
 *   ariaLabel  overrides the generated text summary
 *   className  extra class on the <figure>
 *
 * A legend appears when series.length > 1 and is omitted when it is 1 — a
 * single series is named by the panel title.
 *
 * The SVG is sized by ResizeObserver, so the viewBox always equals the
 * container's own pixel box: it re-renders correctly from 320px to 1400px and
 * never carries a fixed pixel width. Text never distorts because the scale
 * stays 1:1.
 *
 * Keyboard: the plot is focusable. ← → walk the points, Home / End jump to the
 * ends, Esc drops the crosshair. The active point is announced through a
 * polite live region.
 */

const PALETTE = ['--mark', '--good', '--ink-2', '--crit'];

const EL_MONTHS = [
  'Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μάι', 'Ιούν',
  'Ιούλ', 'Αύγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ',
];

const PAD_TOP = 12;
const PAD_RIGHT = 14;
const PAD_BOTTOM = 24;
const MIN_WIDTH = 260;

function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function defaultFormat(v) {
  return Number(v).toLocaleString('el-GR');
}

function defaultXFormat(raw) {
  const s = raw === null || raw === undefined ? '' : String(raw);
  const m = /^(\d{4})-(\d{1,2})/.exec(s);
  if (!m) return s;
  const idx = Number(m[2]) - 1;
  const name = EL_MONTHS[idx];
  if (!name) return s;
  return name + ' ' + m[1].slice(2);
}

/**
 * Human ticks: 1 / 2 / 5 x 10^n, always starting at zero.
 * `integer` keeps the step at 1 or more when the data is whole numbers, so a
 * count axis never grows a 0,5 tick.
 */
function niceTicks(top, target, integer) {
  if (!(top > 0)) return { max: integer ? 1 : 1, ticks: [0, 1] };

  const rough = top / Math.max(1, target);
  const exp = Math.floor(Math.log10(rough));
  const base = Math.pow(10, exp);
  const f = rough / base;
  let step = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * base;
  if (integer && step < 1) step = 1;

  const count = Math.max(1, Math.ceil(top / step - 1e-9));
  const ticks = [];
  for (let i = 0; i <= count; i += 1) ticks.push(step * i);

  return { max: step * count, ticks };
}

function pathFrom(points) {
  let d = '';
  let pen = false;
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (!p) { pen = false; continue; }
    d += (pen ? 'L' : 'M') + p.x.toFixed(2) + ' ' + p.y.toFixed(2) + ' ';
    pen = true;
  }
  return d.trim();
}

/** Area is built per unbroken run so a hole stays a hole. */
function areaFrom(points, baseY) {
  let d = '';
  let run = [];
  const flush = () => {
    if (run.length === 0) return;
    if (run.length === 1) {
      // A lone point has no area to speak of; a 1px sliver would just be noise.
      run = [];
      return;
    }
    d += 'M' + run[0].x.toFixed(2) + ' ' + baseY.toFixed(2) + ' ';
    for (let i = 0; i < run.length; i += 1) {
      d += 'L' + run[i].x.toFixed(2) + ' ' + run[i].y.toFixed(2) + ' ';
    }
    d += 'L' + run[run.length - 1].x.toFixed(2) + ' ' + baseY.toFixed(2) + ' Z ';
    run = [];
  };
  for (let i = 0; i < points.length; i += 1) {
    if (points[i]) run.push(points[i]);
    else flush();
  }
  flush();
  return d.trim();
}

/**
 * fillMonthGaps(rows, opts) — makes a monthly series continuous.
 *
 *   rows    [{ ym: '2026-01', registrations: 13, ... }]  ascending, sparse
 *   opts.xKey    field holding 'YYYY-MM'                 (default 'ym')
 *   opts.keys    numeric fields to zero-fill (default: every non-x key seen)
 *   opts.months  force a window of N months ending at `end`
 *   opts.end     'YYYY-MM' the window ends on (default: last row, else today)
 *
 * Returns a new ascending array with one row per month. Inserted rows carry
 * `__filled: true` so the caller can caption them if it wants to.
 */
export function fillMonthGaps(rows, opts) {
  const o = opts || {};
  const xKey = o.xKey || 'ym';
  const list = Array.isArray(rows) ? rows.slice() : [];

  const parse = (s) => {
    const m = /^(\d{4})-(\d{1,2})/.exec(String(s || ''));
    if (!m) return null;
    return Number(m[1]) * 12 + (Number(m[2]) - 1);
  };
  const stamp = (n) => {
    const y = Math.floor(n / 12);
    const mo = (n % 12) + 1;
    return y + '-' + (mo < 10 ? '0' + mo : String(mo));
  };

  const known = new Map();
  let lo = null;
  let hi = null;
  for (let i = 0; i < list.length; i += 1) {
    const n = parse(list[i] && list[i][xKey]);
    if (n === null) continue;
    known.set(n, list[i]);
    if (lo === null || n < lo) lo = n;
    if (hi === null || n > hi) hi = n;
  }

  let keys = o.keys;
  if (!Array.isArray(keys) || keys.length === 0) {
    const seen = new Set();
    for (let i = 0; i < list.length; i += 1) {
      Object.keys(list[i] || {}).forEach((k) => { if (k !== xKey) seen.add(k); });
    }
    keys = Array.from(seen);
  }

  const now = new Date();
  const today = now.getFullYear() * 12 + now.getMonth();

  let end = o.end ? parse(o.end) : null;
  if (end === null || end === undefined) end = hi === null ? today : hi;

  let start;
  if (o.months && Number(o.months) > 0) start = end - (Number(o.months) - 1);
  else start = lo === null ? end : lo;

  if (start > end) start = end;

  const out = [];
  for (let n = start; n <= end; n += 1) {
    const hit = known.get(n);
    if (hit) { out.push(hit); continue; }
    const blank = { __filled: true };
    blank[xKey] = stamp(n);
    for (let k = 0; k < keys.length; k += 1) blank[keys[k]] = 0;
    out.push(blank);
  }
  return out;
}

export default function TimeChart({
  data,
  series,
  xKey = 'ym',
  height = 220,
  format,
  xFormat,
  area,
  loading = false,
  empty = 'Δεν υπάρχουν δεδομένα για αυτή την περίοδο.',
  caption,
  ariaLabel,
  className,
}) {
  // Two TimeCharts on one screen must not share a gradient id.
  const uid = useId().replace(/:/g, '');
  const holdRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(720);
  const [active, setActive] = useState(-1);

  useMeasureEffect(() => {
    const node = holdRef.current;
    if (!node) return undefined;

    const read = () => {
      const w = node.clientWidth;
      if (w > 0) setWidth(Math.max(MIN_WIDTH, Math.round(w)));
    };
    read();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const lines = useMemo(() => {
    const raw = Array.isArray(series) ? series : [];
    return raw.map((s, i) => ({
      key: s.key,
      label: s.label || s.key,
      color: s.color || PALETTE[i % PALETTE.length],
      format: typeof s.format === 'function' ? s.format : null,
    }));
  }, [series]);

  const rows = Array.isArray(data) ? data : [];

  const fmt = useCallback(
    (value, line, row) => {
      if (line && line.format) return line.format(value, row);
      if (typeof format === 'function') return format(value, line ? line.key : null, row);
      return defaultFormat(value);
    },
    [format]
  );

  const fmtX = useCallback(
    (raw, row) => (typeof xFormat === 'function' ? xFormat(raw, row) : defaultXFormat(raw)),
    [xFormat]
  );

  const model = useMemo(() => {
    const n = rows.length;
    const values = lines.map((line) => rows.map((r) => toNum(r ? r[line.key] : null)));

    let top = 0;
    let any = false;
    let allInt = true;
    for (let s = 0; s < values.length; s += 1) {
      for (let i = 0; i < values[s].length; i += 1) {
        const v = values[s][i];
        if (v === null) continue;
        any = true;
        if (v > top) top = v;
        if (!Number.isInteger(v)) allInt = false;
      }
    }

    const tickTarget = height >= 260 ? 5 : 4;
    const { max, ticks } = niceTicks(top, tickTarget, allInt);

    // Left gutter follows the widest y label so the numbers never clip.
    let widest = 0;
    const tickText = ticks.map((t) => {
      const txt = defaultFormat(t);
      if (txt.length > widest) widest = txt.length;
      return txt;
    });
    const padLeft = Math.min(72, Math.max(30, widest * 6.6 + 12));

    const innerW = Math.max(10, width - padLeft - PAD_RIGHT);
    const innerH = Math.max(10, height - PAD_TOP - PAD_BOTTOM);
    const baseY = PAD_TOP + innerH;

    const xAt = (i) => (n <= 1 ? padLeft + innerW / 2 : padLeft + (innerW * i) / (n - 1));
    const yAt = (v) => baseY - (max > 0 ? (v / max) * innerH : 0);

    const points = values.map((col) =>
      col.map((v, i) => (v === null ? null : { x: xAt(i), y: yAt(v) }))
    );

    // Thin the x labels so they cannot collide: keep the last, walk back in
    // even strides, keep the first unless it lands on top of its neighbour.
    const slot = 66;
    const room = Math.max(2, Math.floor(innerW / slot));
    const every = Math.max(1, Math.ceil(n / room));
    const keep = new Set();
    if (n > 0) {
      keep.add(n - 1);
      for (let i = n - 1 - every; i > 0; i -= every) keep.add(i);
      let lowest = n - 1;
      keep.forEach((i) => { if (i < lowest) lowest = i; });
      if (n === 1) keep.add(0);
      else if (lowest >= every * 0.6) keep.add(0);
      else {
        // The first label would collide with its neighbour — drop the
        // neighbour, never the anchor. Unless the neighbour is the last
        // point, which always keeps its label.
        if (keep.size > 1 && lowest !== n - 1) keep.delete(lowest);
        keep.add(0);
      }
    }

    return {
      n, values, points, max, ticks, tickText,
      padLeft, innerW, innerH, baseY, xAt, keep, any,
    };
  }, [rows, lines, width, height]);

  const showArea = area === undefined ? lines.length === 1 : !!area;

  const summary = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    if (!model.n || !model.any) return 'Γράφημα χρονοσειράς χωρίς δεδομένα.';
    const names = lines.map((l) => l.label).join(', ');
    const first = fmtX(rows[0] ? rows[0][xKey] : '', rows[0]);
    const last = fmtX(rows[model.n - 1] ? rows[model.n - 1][xKey] : '', rows[model.n - 1]);
    const tail = lines
      .map((line, s) => {
        const col = model.values[s];
        const lastVal = col[model.n - 1];
        return line.label + ' ' + (lastVal === null ? '—' : fmt(lastVal, line, rows[model.n - 1]));
      })
      .join(', ');
    return (
      'Γράφημα χρονοσειράς. ' + names + '. ' + model.n + ' σημεία, από ' + first +
      ' έως ' + last + '. Άξονας από 0 έως ' + defaultFormat(model.max) +
      '. Τελευταία τιμή: ' + tail + '.'
    );
  }, [ariaLabel, model, lines, rows, xKey, fmt, fmtX]);

  const pickFromClientX = useCallback(
    (clientX) => {
      const svg = svgRef.current;
      if (!svg || model.n === 0) return;
      const box = svg.getBoundingClientRect();
      if (!box.width) return;
      const x = (clientX - box.left) * (width / box.width);
      if (model.n === 1) { setActive(0); return; }
      const stepX = model.innerW / (model.n - 1);
      let i = Math.round((x - model.padLeft) / (stepX || 1));
      if (i < 0) i = 0;
      if (i > model.n - 1) i = model.n - 1;
      setActive(i);
    },
    [model, width]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (model.n === 0) return;
      let next = active;
      if (e.key === 'ArrowRight') next = active < 0 ? 0 : Math.min(model.n - 1, active + 1);
      else if (e.key === 'ArrowLeft') next = active < 0 ? model.n - 1 : Math.max(0, active - 1);
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = model.n - 1;
      else if (e.key === 'Escape') next = -1;
      else return;
      e.preventDefault();
      setActive(next);
    },
    [active, model.n]
  );

  /* ---------- loading ---------- */
  if (loading) {
    return (
      <figure className={className ? 'chart ' + className : 'chart'} aria-busy="true">
        <div className="chart-skeleton skeleton" style={{ height: height + 'px' }} />
        {lines.length > 1 ? (
          <div className="chart-legend" aria-hidden="true">
            {lines.map((l) => (
              <span className="chart-legend-item" key={l.key}>
                <span className="chart-swatch skeleton" />
                <span className="skeleton" style={{ width: '64px', height: '10px' }} />
              </span>
            ))}
          </div>
        ) : null}
      </figure>
    );
  }

  /* ---------- empty ---------- */
  if (model.n === 0 || !model.any) {
    return (
      <figure className={className ? 'chart ' + className : 'chart'}>
        <div className="empty" style={{ minHeight: height + 'px', display: 'grid', placeItems: 'center' }}>
          {empty}
        </div>
        {caption ? <figcaption className="chart-cap">{caption}</figcaption> : null}
      </figure>
    );
  }

  const activeRow = active >= 0 ? rows[active] : null;
  const activeX = active >= 0 ? model.xAt(active) : 0;
  const tipLeft = Math.min(Math.max(activeX, 72), Math.max(72, width - 72));

  const liveText =
    activeRow
      ? fmtX(activeRow[xKey], activeRow) + ': ' +
        lines
          .map((line, s) => {
            const v = model.values[s][active];
            return line.label + ' ' + (v === null ? '—' : fmt(v, line, activeRow));
          })
          .join(', ')
      : '';

  return (
    <figure className={className ? 'chart ' + className : 'chart'}>
      <div className="chart-plot" ref={holdRef}>
        <svg
          ref={svgRef}
          className="chart-svg"
          viewBox={'0 0 ' + width + ' ' + height}
          width="100%"
          height={height}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={summary}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onBlur={() => setActive(-1)}
          onPointerMove={(e) => pickFromClientX(e.clientX)}
          onPointerDown={(e) => pickFromClientX(e.clientX)}
          onPointerLeave={() => setActive(-1)}
        >
          {/* No <svg><title>: role="img" + aria-label already names the plot
              for assistive tech, and a direct <title> child also raises a
              native browser tooltip — which would fire on the same hover that
              drives the crosshair tooltip and cover it with the whole summary
              sentence. One tooltip per chart, and it is the one with the
              reading under the pointer. */}
          {showArea && lines.length === 1 ? (
            <defs>
              <linearGradient id={'tc-fade-' + uid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" style={{ stopColor: 'var(' + lines[0].color + ')', stopOpacity: 0.20 }} />
                <stop offset="100%" style={{ stopColor: 'var(' + lines[0].color + ')', stopOpacity: 0 }} />
              </linearGradient>
            </defs>
          ) : null}

          {/* gridlines — horizontal only, no vertical grid, no border */}
          {model.ticks.map((t, i) => {
            const y = model.baseY - (model.max > 0 ? (t / model.max) * model.innerH : 0);
            return (
              <line
                key={'g' + i}
                className={t === 0 ? 'chart-base' : 'chart-grid'}
                x1={model.padLeft}
                x2={width - PAD_RIGHT}
                y1={Math.round(y) + 0.5}
                y2={Math.round(y) + 0.5}
              />
            );
          })}

          {model.ticks.map((t, i) => {
            const y = model.baseY - (model.max > 0 ? (t / model.max) * model.innerH : 0);
            return (
              <text key={'t' + i} className="chart-ylab" x={model.padLeft - 8} y={y + 3.5}>
                {model.tickText[i]}
              </text>
            );
          })}

          {rows.map((r, i) => {
            if (!model.keep.has(i)) return null;
            const x = model.xAt(i);
            const anchor = i === 0 && model.n > 1 ? 'start' : i === model.n - 1 && model.n > 1 ? 'end' : 'middle';
            return (
              <text
                key={'x' + i}
                className="chart-xlab"
                x={x}
                y={height - 7}
                style={{ textAnchor: anchor }}
              >
                {fmtX(r[xKey], r)}
              </text>
            );
          })}

          {active >= 0 ? (
            <line
              className="chart-cross"
              x1={Math.round(activeX) + 0.5}
              x2={Math.round(activeX) + 0.5}
              y1={PAD_TOP}
              y2={model.baseY}
            />
          ) : null}

          {showArea && lines.length === 1 ? (
            <path
              className="chart-area"
              d={areaFrom(model.points[0], model.baseY)}
              fill={'url(#tc-fade-' + uid + ')'}
            />
          ) : null}

          {lines.map((line, s) => (
            <path
              key={'l' + line.key}
              className="chart-line"
              pathLength="1"
              d={pathFrom(model.points[s])}
              style={{ stroke: 'var(' + line.color + ')', animationDelay: s * 90 + 'ms' }}
            />
          ))}

          {/* An ISOLATED point — the series has exactly one reading, or this
              reading has a hole on both sides — produces a lone moveto
              ("M373.00 36.53"). A subpath with no path segments is never
              stroked, so without this the value would be silently invisible
              while the axes and the tick labels still claimed a chart. Each
              such point gets an explicit marker instead (r 4 -> 8px). */}
          {lines.map((line, s) => {
            const col = model.points[s];
            return col.map((p, i) => {
              if (!p) return null;
              if (col[i - 1] || col[i + 1]) return null;
              return (
                <circle
                  key={'i' + line.key + '-' + i}
                  className="chart-iso"
                  cx={p.x}
                  cy={p.y}
                  r="4"
                  style={{ fill: 'var(' + line.color + ')', animationDelay: s * 90 + 'ms' }}
                />
              );
            });
          })}

          {active >= 0
            ? lines.map((line, s) => {
                const p = model.points[s][active];
                if (!p) return null;
                return (
                  <circle
                    key={'d' + line.key}
                    className="chart-dot"
                    cx={p.x}
                    cy={p.y}
                    r="4.5"
                    style={{ fill: 'var(' + line.color + ')' }}
                  />
                );
              })
            : null}

          <rect
            className="chart-hit"
            x={model.padLeft}
            y={PAD_TOP}
            width={model.innerW}
            height={model.innerH}
          />
        </svg>

        {active >= 0 ? (
          <div className="chart-tip" style={{ left: tipLeft + 'px', top: '8px' }} role="presentation">
            <div className="chart-tip-head">{fmtX(activeRow[xKey], activeRow)}</div>
            {lines.map((line, s) => {
              const v = model.values[s][active];
              return (
                <div className="chart-tip-row" key={'p' + line.key}>
                  <span className="chart-swatch" style={{ background: 'var(' + line.color + ')' }} />
                  <span className="chart-tip-key">{line.label}</span>
                  <span className="chart-tip-val">{v === null ? '—' : fmt(v, line, activeRow)}</span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <p className="chart-sr" aria-live="polite">{liveText}</p>

      {lines.length > 1 ? (
        <div className="chart-legend">
          {lines.map((line) => (
            <span className="chart-legend-item" key={'g' + line.key}>
              <span className="chart-swatch" style={{ background: 'var(' + line.color + ')' }} />
              {line.label}
            </span>
          ))}
        </div>
      ) : null}

      {caption ? <figcaption className="chart-cap">{caption}</figcaption> : null}
    </figure>
  );
}
